import { useEffect, type ReactNode } from "react";
import type { JSX } from "react";
import { useStore } from "zustand";
import { AgentPanel } from "../agent/AgentPanel";
import { ThreePanel } from "../components/layout";
import { MODULE_LAYOUT_PANE_WIDTHS_STORAGE_KEY } from "../components/layout/layoutMetrics";
import { useAppRuntime } from "../providers/AppRuntimeProvider";
import { useRouterContext } from "../providers/RouterProvider";
import { useUiStore } from "../store/uiStore";

// eslint-disable-next-line @typescript-eslint/no-unnecessary-type-parameters, react-refresh/only-export-components
export function useListSelection<T extends { id: string }>(
  items: readonly T[],
  selectedId: string | undefined,
  setSelectedId: (id: string | undefined) => void,
): void {
  const router = useRouterContext();

  useEffect(() => {
    if (router.pendingItemId) {
      setSelectedId(router.pendingItemId);
      router.consumePendingItem();
    }
  }, [router, setSelectedId]);

  useEffect(() => {
    const firstItem = items[0];

    if (!selectedId && firstItem) {
      setSelectedId(firstItem.id);
      return;
    }

    // Preserve explicit selection while the list is still empty/loading.
    // Clearing it here breaks direct URLs like #/inbox/episode/:id on a full reload.
    if (selectedId && items.length === 0) {
      return;
    }

    // When the URL carries a specific entity id, respect it — even if it's
    // not in the current filtered list (e.g. status="completed" episodes
    // are hidden from the timeline, or the id is stale after a DB reset).
    // Auto-correcting here fights the URL→store sync effect in parent
    // modules and produces an infinite render loop.
    if (
      selectedId &&
      items.length > 0 &&
      !items.some((item) => item.id === selectedId) &&
      !router.entityId
    ) {
      setSelectedId(firstItem?.id);
    }
  }, [items, selectedId, setSelectedId, router.entityId]);
}

// eslint-disable-next-line react-refresh/only-export-components
export function useSearchFilter<T>(
  items: readonly T[],
  searchQuery: string,
  matchFn: (item: T, query: string) => boolean,
): readonly T[] {
  if (!searchQuery) {
    return items;
  }

  const normalizedQuery = searchQuery.toLowerCase();
  return items.filter((item) => matchFn(item, normalizedQuery));
}

// ─── Primary layout ─────────────────────────────────────────────────
//  [ list pane | detail pane | agent panel ]

export interface ModuleLayoutProps {
  readonly moduleName: string;
  readonly listPane: ReactNode;
  readonly detailPane: ReactNode;
  readonly rightPane?: ReactNode;
  readonly defaultSidebarEnabled?: boolean;
  readonly containerClassName?: string;
  readonly detailPaneClassName?: string;
  readonly listHandleBackground?: string;
  readonly sidebarHandleBackground?: string;
}

export function ModuleLayout({
  moduleName,
  listPane,
  detailPane,
  rightPane,
  defaultSidebarEnabled = true,
  containerClassName,
  detailPaneClassName,
  listHandleBackground,
  sidebarHandleBackground,
}: ModuleLayoutProps): JSX.Element {
  const runtime = useAppRuntime();
  const activeContext = useStore(runtime.agent.store, (s) => s.activeContext);
  const agentPanelVisible = useUiStore((s) => s.agentPanelVisible);

  useEffect(() => {
    runtime.agent.setActiveContext(null);
  }, [moduleName, runtime]);

  const sidebar = agentPanelVisible
    ? rightPane ?? (defaultSidebarEnabled ? (
        <AgentPanel
          moduleName={moduleName}
          selectedItemId={activeContext?.entityId}
          selectedItemTitle={activeContext?.entityTitle}
          selectedChatId={activeContext?.chatId}
          selectedChatName={activeContext?.chatTitle}
          contextTitle="Context"
          // @tested-by: tst_fe_demo_prompts_003
          // @invariant: generic graph-wide demo prompts must not appear next
          // to an entity-specific conversation or detail view.
          showDemoPresets={false}
        />
      ) : undefined)
    : undefined;

  return (
    <ThreePanel
      listPane={listPane}
      detailPane={detailPane}
      containerClassName={containerClassName}
      detailPaneClassName={detailPaneClassName}
      listHandleBackground={listHandleBackground}
      sidebarHandleBackground={sidebarHandleBackground}
      sidebarPane={sidebar}
      storageKey={MODULE_LAYOUT_PANE_WIDTHS_STORAGE_KEY}
      sidebarPaneClassName="bg-[#1E1E1E] overflow-hidden"
    />
  );
}
